import { AxiosError } from 'axios';

/**
 * Normalized API error shape. Backend returns `{ error, message, errors[] }`
 * inconsistently across endpoints — this collapses them so callers and form
 * adapters see a single contract.
 */
export interface ApiError {
  status: number;
  message: string;
  /** Field-level validation errors keyed by form field name. */
  fieldErrors?: Record<string, string>;
  /** Original axios payload, for breadcrumb / debug surfaces. */
  raw?: unknown;
}

interface BackendValidationError {
  field?: string;
  path?: string | (string | number)[];
  message?: string;
  msg?: string;
}

type BackendValidationIssue = BackendValidationError | string;

interface BackendErrorBody {
  error?: string;
  message?: string;
  success?: boolean;
  errors?: BackendValidationIssue[] | Record<string, string>;
  /** Legacy Zod envelope used by some endpoints before normalization. */
  details?: BackendValidationIssue[];
  data?: unknown;
}

function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    'message' in error &&
    typeof (error as ApiError).message === 'string'
  );
}

/** Convert any unknown error (axios or otherwise) into a stable ApiError. */
export function toApiError(error: unknown): ApiError {
  if (isApiError(error)) {
    const normalizedRaw = normalizeBackendErrorBody(error.raw);
    const normalizedMessage =
      isGenericHttpMessage(error.message)
        ? (
            extractMessageFromUnknown(normalizedRaw) ||
            extractMessageFromUnknown(error)
          )
        : undefined;

    return normalizedMessage ? { ...error, message: normalizedMessage, raw: normalizedRaw } : error;
  }

  if (error instanceof AxiosError || isAxiosErrorLike(error)) {
    const status = error.response?.status ?? 0;
    const body = normalizeBackendErrorBody(error.response?.data);
    const validationIssues = body?.errors ?? body?.details;
    const fieldErrors = collectFieldErrors(validationIssues);

    const message =
      extractMessageFromUnknown(body) ||
      extractMessageFromUnknown(error) ||
      error.message ||
      'Request failed';

    return { status, message, fieldErrors, raw: body };
  }

  if (error instanceof Error) {
    return { status: 0, message: extractMessageFromUnknown(error) || error.message };
  }

  return { status: 0, message: extractMessageFromUnknown(error) || 'Unknown error' };
}

function isAxiosErrorLike(
  error: unknown,
): error is {
  isAxiosError?: boolean;
  message?: string;
  response?: { status?: number; data?: unknown };
} {
  return (
    typeof error === 'object' &&
    error !== null &&
    (
      (Boolean((error as { isAxiosError?: boolean }).isAxiosError)) ||
      ('response' in error && 'message' in error)
    )
  );
}

function normalizeBackendErrorBody(body: unknown): BackendErrorBody | undefined {
  if (!body) return undefined;

  if (typeof body === 'string') {
    try {
      const parsed = JSON.parse(body) as unknown;
      return normalizeBackendErrorBody(parsed) ?? { message: body };
    } catch {
      return { message: body };
    }
  }

  if (typeof body === 'object') {
    return body as BackendErrorBody;
  }

  return undefined;
}

function extractBackendMessage(body: BackendErrorBody | undefined): string | undefined {
  if (!body) return undefined;

  const directMessage =
    firstMeaningfulString(body.message) ||
    preferredErrorString(body.error);
  if (directMessage) return directMessage;

  const nested = normalizeBackendErrorBody(body.data);
  if (!nested || nested === body) return undefined;

  return (
    firstMeaningfulString(nested.message) ||
    preferredErrorString(nested.error) ||
    formatValidationMessage(nested.errors ?? nested.details)
  );
}

function extractMessageFromUnknown(value: unknown, depth = 0): string | undefined {
  if (depth > 4 || value == null) return undefined;

  const normalized = normalizeBackendErrorBody(value);
  const normalizedMessage =
    extractBackendMessage(normalized) ||
    formatValidationMessage(normalized?.errors ?? normalized?.details) ||
    preferredErrorString(normalized?.error);
  if (normalizedMessage) return normalizedMessage;

  if (typeof value === 'object' && value !== null) {
    const record = value as Record<string, unknown>;
    return (
      extractMessageFromUnknown(record.raw, depth + 1) ||
      extractMessageFromUnknown(record.response, depth + 1) ||
      extractMessageFromUnknown(record.data, depth + 1) ||
      extractMessageFromUnknown(record.cause, depth + 1) ||
      extractMessageFromUnknown(record.error, depth + 1)
    );
  }

  return undefined;
}

function preferredErrorString(value: unknown): string | undefined {
  const text = firstMeaningfulString(value);
  if (!text) return undefined;
  return text !== 'Validation failed' ? text : undefined;
}

function firstMeaningfulString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function isGenericHttpMessage(message: string): boolean {
  return /^Request failed with status code \d{3}$/.test(message.trim());
}

function validationIssueKey(issue: BackendValidationError): string | undefined {
  if (typeof issue.field === 'string' && issue.field.length > 0) {
    return issue.field;
  }
  if (Array.isArray(issue.path) && issue.path.length > 0) {
    return issue.path.map(String).join('.');
  }
  if (typeof issue.path === 'string' && issue.path.length > 0) {
    return issue.path;
  }
  return undefined;
}

function validationIssueMessage(issue: BackendValidationIssue): string | undefined {
  if (typeof issue === 'string') return firstMeaningfulString(issue);
  return issue.message ?? issue.msg;
}

function formatValidationMessage(
  errors: BackendValidationIssue[] | Record<string, string> | undefined,
): string | undefined {
  if (!errors) return undefined;

  if (Array.isArray(errors)) {
    const parts = errors
      .map((issue) => {
        if (typeof issue === 'string') return firstMeaningfulString(issue);
        const key = validationIssueKey(issue);
        const msg = validationIssueMessage(issue);
        if (!msg) return undefined;
        return key ? `${key}: ${msg}` : msg;
      })
      .filter((part): part is string => !!part);
    return parts.length > 0 ? parts.join('; ') : undefined;
  }

  if (typeof errors === 'object') {
    const parts = Object.entries(errors).map(([key, msg]) => `${key}: ${msg}`);
    return parts.length > 0 ? parts.join('; ') : undefined;
  }

  return undefined;
}

function collectFieldErrors(
  errors: BackendValidationIssue[] | Record<string, string> | undefined,
): Record<string, string> | undefined {
  if (!errors) return undefined;

  if (Array.isArray(errors)) {
    const map: Record<string, string> = {};
    for (const e of errors) {
      if (typeof e === 'string') continue;
      const key = validationIssueKey(e);
      const msg = validationIssueMessage(e);
      if (key && msg) map[key] = msg;
    }
    return Object.keys(map).length > 0 ? map : undefined;
  }

  if (typeof errors === 'object') {
    return errors as Record<string, string>;
  }

  return undefined;
}

/**
 * Quick predicates for common HTTP states. Used in interceptors and route
 * guards to decide whether to redirect, refresh, or surface a toast.
 */
export const isUnauthorized = (e: ApiError): boolean => e.status === 401;
export const isForbidden = (e: ApiError): boolean => e.status === 403;
export const isValidation = (e: ApiError): boolean => e.status === 422 || !!e.fieldErrors;
export const isServer = (e: ApiError): boolean => e.status >= 500;
export const isNetwork = (e: ApiError): boolean => e.status === 0;
